import OperationModel                              from 'Models/intervention/OperationModel';
import RiskAssessmentModel                         from 'Models/intervention/RiskAssessmentModel';
import TaskModel                                   from 'Models/intervention/TaskModel';
import TaskOperationStatusModel                    from 'Models/intervention/TaskOperationStatusModel';
import TaskZoneModel                               from 'Models/intervention/TaskZoneModel';
import ContractIterationItemEventItemActivityModel from 'Models/sales/ContractIterationItemEventItemActivityModel';
import QuotationItemEventItemActivityModel         from 'Models/sales/QuotationItemEventItemActivityModel';
import doc                                         from 'decorators/doc';
import model                                       from 'decorators/model';
import nestedResolvable                            from 'decorators/nestedResolvable';
import { Timestampable }                           from 'helpers/traits';
import { Blamable }                                from 'helpers/traits';
import { computed }                                from 'mobx';
import InterventionPrivateApiModel                 from 'modelx/models/abstracts/InterventionPrivateApiModel';
import moment                                      from 'moment';

type InterventionTaskOperationStatusReference = TaskOperationStatusReference;

const customDoc = doc;

@model.staticLabel('Prestation', false)
@model.urnResource('task_operation')
@doc.path('/task_operations/{?id}')
export default class TaskOperationModel extends Blamable(Timestampable(InterventionPrivateApiModel)) {

	public _filters: ModelFiltersExtended<{
		'createdAt[after]': string;
		'createdAt[before]': string;
		'createdAt[strictly_after]': string;
		'createdAt[strictly_before]': string;
		'exists[mainTaskOperationExtras]': '0' | '1';
		'exists[taskOperationExtras]': '0' | '1';
		'mainTaskOperationExtras': id;
		'mainTaskOperationExtras.mainTaskOperation': id;
		'mainTaskOperationExtras.mainTaskOperation.task.taskGroup': id;
		'mainTaskOperationExtras.mainTaskOperation.task.taskGroup.sourceUrn': Urn;
		'mainTaskOperationExtras.mainTaskOperation.task.taskHolder': id;
		'mainTaskOperationExtras.mainTaskOperation.task.taskHolder.holderUrn': Urn;
		'operation': id;
		'operation.constraintGroups.constraintGroupProperties.property': id;
		'operation.constraintGroups.constraintGroupProperties.property.propertyType': id;
		'operation.operationType': id;
		'operation.operationType.operationCategory': id;
		'scheduleEndDate[after]': string | Date;
		'scheduleEndDate[before]': string | Date;
		'scheduleEndDate[strictly_after]': string | Date;
		'scheduleEndDate[strictly_before]': string | Date;
		'scheduleStartDate[after]': string | Date;
		'scheduleStartDate[before]': string | Date;
		'scheduleStartDate[strictly_after]': string | Date;
		'scheduleStartDate[strictly_before]': string | Date;
		'sourceUrn': Urn;
		'tags[serviceUrn][tagReference]': id;
		'task': id;
		'task.owner.clientUrn': Urn;
		'task.owner.enterprisePartitionUrn': Urn;
		'task.owner.enterpriseUrn': Urn;
		'task.owner.ownerSubPartition': id;
		'task.owner.ownerSubPartition.partitionUrn': Urn;
		'task.owner.ownerSubPartition.subPartitionUrn': Urn;
		'task.tags[serviceUrn][tagReference]': id;
		'task.taskGroup': id;
		'task.taskGroup.sourceUrn': Urn;
		'task.taskHolder': id;
		'task.taskHolder.holderUrn': Urn;
		'task.taskScheduleConstraints.weekday': number;
		'taskOperationArticles': id;
		'taskOperationExtras': id;
		'taskOperationExtras.extraTaskOperation': id;
		'taskOperationExtras.taskOperationExtraReason': id;
		'taskOperationInstallations': id;
		'taskOperationInstallations.installation': id;
		'taskOperationStatus': id;
		'taskOperationStatus.reference': InterventionTaskOperationStatusReference;
		'taskOperationTimelogs': id;
		'taskZone': id;
		'taskZone.city': string;
		'taskZone.tags[serviceUrn][tagReference]': id;
		'taskZone.zipCode': string;
		'updatedAt[after]': string | Date;
		'updatedAt[before]': string | Date;
		'updatedAt[strictly_after]': string | Date;
		'updatedAt[strictly_before]': string | Date;
		'value[between]': string;
		'value[gt]': string;
		'value[gte]': string;
		'value[lt]': string;
		'value[lte]': string;
		'wantProperty': string;
		'tags[$:registry:service:intervention][schedule_option]': Urn;
		'task.taskHolder.tags[$:registry:service:intervention][contract_urn]': Urn;
	}> = {};

	public _sorts: ModelSortsExtended<{
		'id': string;
		'scheduleEndDate': string;
		'scheduleStartDate': string;
		'task.title': string;
		'taskZone.city': string;
		'taskZone.name': string;
		'taskZone.zipCode': string;
		'updatedAt': string;
	}> = {};

	@doc.string declare label: string;
	@doc.number declare labourHourlyCost: number;
	@doc.string declare note: string;
	@doc.model(OperationModel) declare operation: OperationModel;
	@doc.model(RiskAssessmentModel) declare riskAssessment: RiskAssessmentModel;
	@doc.date declare scheduleEndDate: Moment;
	@doc.date declare scheduleStartDate: Moment;
	@doc.model(TaskModel) declare task: TaskModel;
	@doc.model(TaskOperationStatusModel) declare taskOperationStatus: TaskOperationStatusModel;
	@doc.model(TaskZoneModel) declare taskZone: TaskZoneModel;
	@doc.number declare value: number;

	@customDoc.modelUrn(ContractIterationItemEventItemActivityModel || QuotationItemEventItemActivityModel) declare source: ContractIterationItemEventItemActivityModel | QuotationItemEventItemActivityModel;

	public get operationId(): id {
		return this.get('operation.id', 0);
	}

	public get taskZoneId(): id {
		return this.get('taskZone.id', 0);
	}

	public get taskId(): id {
		return this.get('task.id', 0);
	}

	public get taskOperationStatusId(): id {
		return this.get('taskOperationStatus.id', 0);
	}

	@computed
	public get isLate() {
		return moment(this.scheduleEndDate).isBefore();
	}

	@nestedResolvable()
	public get operationType() {
		return this.operation.operationType;
	}

	@nestedResolvable()
	public get operationCategory() {
		return this.operation.operationType.operationCategory;
	}

	@nestedResolvable()
	public get company() {
		return this.taskZone.company;
	}

	@nestedResolvable()
	public get owner() {
		return this.taskZone.owner;
	}

	/**
	 * Permet de savoir s'il est possible de planifier une intervention pour cette TaskOperation
	 */
	public get isDisabledAddIntervention() {
		const statusForDisabled: TaskOperationStatusReference[] = ['cancelled'];
		return statusForDisabled.includes(this.taskOperationStatus.reference);
	}

	/*
		En ce qui concerne les quantités, le backend renverra un nombre si l'option est activée
		de chiffrer les unités de prestation coté exploitation (intervention, pointage technicien...).
		Sinon, les quantitées seront nulles.
	*/
	public get expectedQuantity(): number | null {
		if (this.get('expectedQuantity')) {
			return parseFloat(this.get('expectedQuantity'));
		}

		return null;
	}

	public get realQuantity(): number | null {
		if (this.get('realQuantity')) {
			return parseFloat(this.get('realQuantity'));
		}

		return null;
	}

	public get maximumQuantity(): number | null {
		if (this.expectedQuantity === null) {
			return null;
		} else {
			return this.expectedQuantity - (this.realQuantity || 0);
		}
	}
}